import { FilterType } from './Types';
import { PROPERTY_TYPES, SALE_TYPE, AVAILABILITY_TYPES, MALLORCA_NOT_DEFINED_LOCATIONS } from './Constants';

export interface FilterValidationResult {
    isValid: boolean;
    cleanedFilter: FilterType;
    removedFields: string[];
    warnings: string[];
    isCompletelyEmpty: boolean;
}

/**
 * Validates a saved filter and removes invalid values
 * @param filter - The filter to validate
 * @returns FilterValidationResult with validation status and cleaned filter
 */
export const validateAndCleanFilter = (filter: FilterType): FilterValidationResult => {
    const cleanedFilter: FilterType = { ...filter };
    const removedFields: string[] = [];
    const warnings: string[] = [];

    // Validate property types
    if (filter.type && filter.type.length > 0) {
        const validTypes = filter.type.filter((type) => PROPERTY_TYPES.includes(type));
        const invalidTypes = filter.type.filter((type) => !PROPERTY_TYPES.includes(type));

        if (invalidTypes.length > 0) {
            removedFields.push(`Invalid property types: ${invalidTypes.join(', ')}`);
            warnings.push(`Removed invalid property types: ${invalidTypes.join(', ')}`);
        }

        cleanedFilter.type = validTypes;
    }

    // Validate listing type
    if (filter.listingType && !SALE_TYPE.includes(filter.listingType)) {
        removedFields.push(`Invalid listing type: ${filter.listingType}`);
        warnings.push(`Removed invalid listing type: ${filter.listingType}`);
        cleanedFilter.listingType = '';
    }

    // Validate availability types
    if (filter.availability && filter.availability.length > 0) {
        const validAvailability = filter.availability.filter((avail) => AVAILABILITY_TYPES.map((type) => type.toLowerCase()).includes(avail.toLowerCase()));
        const invalidAvailability = filter.availability.filter((avail) => !AVAILABILITY_TYPES.map((type) => type.toLowerCase()).includes(avail.toLowerCase()));

        if (invalidAvailability.length > 0) {
            removedFields.push(`Invalid availability types: ${invalidAvailability.join(', ')}`);
            warnings.push(`Removed invalid availability types: ${invalidAvailability.join(', ')}`);
        }

        cleanedFilter.availability = validAvailability.length > 0 ? validAvailability : ['available'];
    }

    // Validate bed/bath indices (should be 0-10 for beds, 0-10 for baths)
    if (filter.bedbath) {
        const validBedIndices = filter.bedbath.bedIdx?.filter((idx) => idx >= 0 && idx <= 10) || [0];
        const validBathIndices = filter.bedbath.bathIdx?.filter((idx) => idx >= 0 && idx <= 10) || [0];

        if (filter.bedbath.bedIdx && filter.bedbath.bedIdx.some((idx) => idx < 0 || idx > 10)) {
            removedFields.push('Invalid bedroom indices');
            warnings.push('Fixed invalid bedroom indices');
        }

        if (filter.bedbath.bathIdx && filter.bedbath.bathIdx.some((idx) => idx < 0 || idx > 10)) {
            removedFields.push('Invalid bathroom indices');
            warnings.push('Fixed invalid bathroom indices');
        }

        cleanedFilter.bedbath = {
            bedIdx: validBedIndices,
            bathIdx: validBathIndices,
        };
    }

    // Validate price ranges (should be non-negative)
    if (filter.price) {
        if (filter.price.from < 0 || filter.price.to < 0) {
            removedFields.push('Invalid price range (negative values)');
            warnings.push('Fixed invalid price range');
            cleanedFilter.price = { from: 0, to: 0 };
        }
    }

    // Validate construction area ranges
    if (filter.constructionArea) {
        if (filter.constructionArea.from < 0 || filter.constructionArea.to < 0) {
            removedFields.push('Invalid construction area range (negative values)');
            warnings.push('Fixed invalid construction area range');
            cleanedFilter.constructionArea = { from: 0, to: 0 };
        }
    }

    // Validate plot area ranges
    if (filter.plotArea) {
        if (filter.plotArea.from < 0 || filter.plotArea.to < 0) {
            removedFields.push('Invalid plot area range (negative values)');
            warnings.push('Fixed invalid plot area range');
            cleanedFilter.plotArea = { from: 0, to: 0 };
        }
    }

    // Validate price per square meter ranges
    if (filter.pricePerSquareMeter) {
        if (filter.pricePerSquareMeter.from < 0 || filter.pricePerSquareMeter.to < 0) {
            removedFields.push('Invalid price per square meter range (negative values)');
            warnings.push('Fixed invalid price per square meter range');
            cleanedFilter.pricePerSquareMeter = { from: 0, to: 0 };
        }
    }

    // Validate locations (only check for basic structure, not specific city names)
    if (filter.locations && filter.locations.length > 0) {
        const validLocations = filter.locations.filter(
            (location) => location && typeof location.city === 'string' && location.city.trim() !== '' && Array.isArray(location.neighborhoods),
        );
        const invalidLocations = filter.locations.filter(
            (location) => !location || typeof location.city !== 'string' || location.city.trim() === '' || !Array.isArray(location.neighborhoods),
        );

        if (invalidLocations.length > 0) {
            removedFields.push(`Invalid location structure: ${invalidLocations.length} locations`);
            warnings.push(`Removed ${invalidLocations.length} invalid location structures`);
        }

        cleanedFilter.locations = validLocations;
        cleanedFilter.city = validLocations.map((loc) => loc.city);
        cleanedFilter.neighborhood = validLocations.flatMap((loc) => loc.neighborhoods);
    }

    // Validate individual city array (only check for basic structure, not specific city names)
    if (filter.city && filter.city.length > 0) {
        const validCities = filter.city.filter((city) => typeof city === 'string' && city.trim() !== '');
        const invalidCities = filter.city.filter((city) => typeof city !== 'string' || city.trim() === '');

        if (invalidCities.length > 0) {
            removedFields.push(`Invalid city structure: ${invalidCities.length} cities`);
            warnings.push(`Removed ${invalidCities.length} invalid city structures`);
        }

        cleanedFilter.city = validCities;
    }

    // Validate state array (if any specific validation is needed)
    if (filter.state && filter.state.length > 0) {
        // For now, just ensure it's an array of strings
        const validStates = filter.state.filter((state) => typeof state === 'string' && state.trim() !== '');
        if (validStates.length !== filter.state.length) {
            removedFields.push('Invalid state values');
            warnings.push('Removed invalid state values');
        }
        cleanedFilter.state = validStates;
    }

    // Validate neighborhood array
    if (filter.neighborhood && filter.neighborhood.length > 0) {
        const validNeighborhoods = filter.neighborhood.filter((neighborhood) => typeof neighborhood === 'string' && neighborhood.trim() !== '');
        if (validNeighborhoods.length !== filter.neighborhood.length) {
            removedFields.push('Invalid neighborhood values');
            warnings.push('Removed invalid neighborhood values');
        }
        cleanedFilter.neighborhood = validNeighborhoods;
    }

    // Check if the filter has any meaningful filters after cleaning
    const hasValidFilters =
        cleanedFilter.type.length > 0 ||
        cleanedFilter.city.length > 0 ||
        cleanedFilter.locations.length > 0 ||
        (cleanedFilter.listingType && cleanedFilter.listingType.length > 0) ||
        (cleanedFilter.availability.length > 0 && !cleanedFilter.availability.every((avail) => avail === 'available')) ||
        cleanedFilter.price.from > 0 ||
        cleanedFilter.price.to > 0 ||
        cleanedFilter.constructionArea.from > 0 ||
        cleanedFilter.constructionArea.to > 0 ||
        cleanedFilter.plotArea.from > 0 ||
        cleanedFilter.plotArea.to > 0 ||
        cleanedFilter.pricePerSquareMeter.from > 0 ||
        cleanedFilter.pricePerSquareMeter.to > 0 ||
        (cleanedFilter.bedbath.bedIdx.length > 0 && cleanedFilter.bedbath.bedIdx.some((idx) => idx > 0)) ||
        (cleanedFilter.bedbath.bathIdx.length > 0 && cleanedFilter.bedbath.bathIdx.some((idx) => idx > 0));

    // A filter is valid if it has no removed fields OR if it still has meaningful filters after cleaning
    const isValid = removedFields.length === 0 || hasValidFilters;

    // Check if the filter is completely empty (only has default values)
    const isCompletelyEmpty = !hasValidFilters;

    return {
        isValid,
        cleanedFilter,
        removedFields,
        warnings,
        isCompletelyEmpty,
    };
};
